import {trackEvent} from '@hconnect/common/logging/Analytics'
import {useFieldConfigurationContext} from '@hconnect/tuc-processes/configuration/useConfigurationContext'
import {useOrderProcessFormContext} from '@hconnect/tuc-processes/form/useOrderProcessFormContext'
import {ContractDto} from '@hconnect/tuc-processes/options/contract.types'
import {useCallback, useMemo} from 'react'
import {useWatch} from 'react-hook-form'

/**
 * Represents a quote for display in the QuoteStep.
 * This is a thin wrapper around ContractDto that provides UI-friendly property names.
 *
 * Note: We could use ContractDto directly, but Quote provides:
 * - Clear naming for UI context (quoteNumber/quoteName vs contractNumber/contractDescription)
 * - Decoupling from API schema changes
 * - Explicit contract of what the UI needs
 */
export interface Quote {
  /** The quote number (maps to contractNumber in API) */
  quoteNumber: string
  /** Display name for the quote - from contractDescription */
  quoteName: string

  /** Display SalesOffice for the quote or while ship-to creation if needed */
  salesOffice: string

  /** Display MarketArea for the quote or while ship-to creation if needed */
  marketArea?: string

  /** Raw SAP contract validity start date (yyyy-MM-dd) */
  contractStartDate?: string

  /** Raw SAP contract validity end date (yyyy-MM-dd) */
  contractEndDate?: string

  /** The full contract object for additional data access */
  contract: ContractDto
}

/**
 * Hook to manage quote (contract) selection in the order intake flow.
 *
 * This hook provides:
 * - List of available quotes/contracts
 * - Currently selected quote
 * - Method to select a quote
 * - Filter functionality for searching quotes
 */
export const useQuote = () => {
  const {control, setValue, trigger} = useOrderProcessFormContext()
  /*
    Create a separate configuration like useFieldConfigurationContext('quote')
    Or rename mix to something more generic like 'contractsAndMaterials'
  */
  const {contracts} = useFieldConfigurationContext('mix')

  const [selectedContract] = useWatch({
    control,
    name: ['contract']
  })

  /**
   * Maps contracts to quotes for display.
   * - Uses contractDescription for the display name, falls back to quoteNumber if not available
   * - Sorts alphabetically by quote name for consistent ordering
   */
  const availableQuotes: Quote[] = useMemo(() => {
    return contracts
      .map((contract) => ({
        quoteNumber: contract.contractNumber,
        quoteName: contract.contractDescription || `Quote ${contract.contractNumber}`,
        salesOffice: contract.salesOffice,
        marketArea: contract.marketArea,
        contractStartDate: contract.contractStartDate,
        contractEndDate: contract.contractEndDate,
        contract
      }))
      .sort((a, b) => a.quoteName.localeCompare(b.quoteName))
  }, [contracts])

  /**
   * Currently selected quote
   */
  const selectedQuote = useMemo(() => {
    if (!selectedContract?.contractNumber) return undefined
    return availableQuotes.find((q) => q.quoteNumber === selectedContract.contractNumber)
  }, [selectedContract?.contractNumber, availableQuotes])

  /**
   * Select a quote by quote number.
   * This will update the form state and prepare for material selection.
   */
  const setQuote = useCallback(
    (quoteNumber: string) => {
      const quote = availableQuotes.find((q) => q.quoteNumber === quoteNumber)

      if (quote) {
        trackEvent('rmcQuoteSelected', {
          quoteNumber,
          businessLine: 'RMC'
        })

        // Store contract data in form state (contractItemPositionNumber is set in MaterialStep)
        setValue('contract', {
          contractNumber: quote.contract.contractNumber,
          orgUnitId: quote.contract.orgUnitId,
          customerNumber: quote.contract.customerNumber,
          salesOffice: quote.contract.salesOffice,
          contractDescription: quote.contract.contractDescription,
          zkPartner: quote.contract.zkPartner
        })

        // Clear material selection when quote changes since materials are contract-specific
        setValue('material', undefined)
        setValue('materialVariations', undefined)

        void trigger(['contract', 'material', 'materialVariations'])
      }
    },
    [availableQuotes, setValue, trigger]
  )

  /**
   * Filter quotes by search term
   * Searches in quote name and quote number
   */
  const filterQuotes = useCallback(
    (searchTerm: string): Quote[] => {
      if (!searchTerm.trim()) return availableQuotes

      const lowerSearch = searchTerm.toLowerCase()
      return availableQuotes.filter(
        (quote) =>
          quote.quoteName.toLowerCase().includes(lowerSearch) ||
          quote.quoteNumber.toLowerCase().includes(lowerSearch)
      )
    },
    [availableQuotes]
  )

  /**
   * Check if there are multiple quotes available
   * This is used to determine if the quote step should be shown
   * Uses de-duplicated count for accurate determination
   */
  const hasMultipleQuotes = availableQuotes.length > 1

  return {
    /** All available quotes */
    availableQuotes,
    /** Currently selected quote */
    selectedQuote,
    /** Select a quote by quote number */
    setQuote,
    /** Filter quotes by search term */
    filterQuotes,
    /** Whether multiple quotes are available */
    hasMultipleQuotes,
    /** Raw contracts for advanced use cases */
    contracts
  }
}
